<!--
  Job Safety Audit Submission Template
  ====================================
  Purpose:
    Renders the single-step Job Safety Audit reactive form. Although the model defines
    multiple conceptual sections, the navigation component detects a single step and hides
    step controls. This template conditionally renders hazard/protection/rescue sections
    based on the `fallExposure` control.

  Key Behaviors (mirrors component logic):
    - When fallExposure is 'No': All checkbox sections are hidden (except exposure itself) and
      upstream submission logic normalizes checkbox values to 'NA'.
    - When fallExposure is blank: Checkbox groups remain disabled until a choice is made.
    - "Other Specify" inputs appear only when their paired *_Other checkbox is selected.
    - Declaration and Working Under Plan section always displayed.

  Maintenance Notes:
    - Each checkbox group `label` attribute falls back to fieldMappings if metadata label absent.
    - If additional checkbox sections are introduced in metadata, corresponding structural blocks
      should follow the same pattern as existing ones (divider + grid + conditional specify input).
    - Keep semantic heading hierarchy (h2 inside section dividers) for accessibility.
-->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <div class="step-content">
      <ion-list>
  <!-- Section: Basic Information -->
        <ion-item-divider class="form-section-title" id="fall-hazards-section">
          <ion-label>
            <h2>{{ basicsSection?.title }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="4">
              <app-date-input
                [templateFormGroup]="basicInfoFormGroup"
                [control]="basicInfoFormGroup.get('date')!"
                [label]="fieldMappings['date']"
                [requiredField]="requiredFields['date']"
                [today]="true"
              ></app-date-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="basicInfoFormGroup"
                [control]="basicInfoFormGroup.get('callNumber')!"
                [label]="fieldMappings['callNumber']"
                [requiredField]="requiredFields['callNumber']"
                placeholder="Enter call number"
              ></app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="basicInfoFormGroup"
                [control]="basicInfoFormGroup.get('location')!"
                [label]="fieldMappings['location']"
                [requiredField]="requiredFields['location']"
                placeholder="Enter location"
              ></app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

  <!-- Section: Fall Hazards (Exposure drives downstream visibility) -->
        <ion-item-divider class="form-section-title" id="fall-hazards-section">
          <ion-label>
            <h2>{{ fallHazardsSection?.title }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <!-- Control: Fall Exposure (Yes/No) determines visibility + validation of subsequent sections -->
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="fallHazardsFormGroup"
                [control]="fallHazardsFormGroup.get('fallExposure')!"
                [label]="fieldMappings['fallExposure']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
                [requiredField]="requiredFields['fallExposure']"
              ></app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Conditional Group: Fall Hazard checkboxes (hidden when exposure explicitly 'No') -->
          <ion-row *ngIf="!noFallExposure()">
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="fallHazardsCheckboxesFormGroup"
                [checkboxes]="fallHazardsCheckboxFields"
                [label]="
                  fallHazardsCheckboxGroup?.checkboxLabel ||
                  fieldMappings['fallHazards']
                "
                [requiredField]="true"
                [disabled]="isFallExposureFieldBlank()"
              ></app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row
            *ngIf="
              fallHazardsCheckboxesFormGroup.get('fallHazardsOther')?.value &&
              !noFallExposure()
            "
          >
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="fallHazardsFormGroup"
                [control]="fallHazardsFormGroup.get('otherFallHazardsSpecify')!"
                [label]="fieldMappings['otherFallHazardsSpecify']"
                [requiredField]="
                  fallHazardsCheckboxesFormGroup.get('fallHazardsOther')?.value
                "
                placeholder="Specify other fall hazard"
              ></app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

          <!-- Conditional Block: Remaining safety-related sections (only when exposure is 'Yes') -->
        <ng-container *ngIf="!noFallExposure()">
          <!-- Section: Methods of Fall Protection -->
          <ion-item-divider
            class="form-section-title"
            id="fall-protection-section"
          >
            <ion-label>
              <h2>{{ fallProtectionSection?.title || "Fall Protection" }}</h2>
            </ion-label>
          </ion-item-divider>
          <ion-grid>
            <ion-row>
              <ion-col>
                <app-checkbox-input
                  [templateFormGroup]="fallProtectionCheckboxesFormGroup"
                  [checkboxes]="fallProtectionCheckboxFields"
                  [label]="
                    fallProtectionCheckboxGroup?.checkboxLabel ||
                    fieldMappings['fallProtection']
                  "
                  [requiredField]="true"
                  [disabled]="isFallExposureFieldBlank()"
                ></app-checkbox-input>
              </ion-col>
            </ion-row>
            <ion-row
              *ngIf="
                fallProtectionCheckboxesFormGroup.get('fallProtectionOther')
                  ?.value
              "
            >
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="fallProtectionFormGroup"
                  [control]="
                    fallProtectionFormGroup.get('otherFallProtectionSpecify')!
                  "
                  [label]="fieldMappings['otherFallProtectionSpecify']"
                  [requiredField]="
                    fallProtectionCheckboxesFormGroup.get('fallProtectionOther')
                      ?.value
                  "
                  placeholder="Specify other fall protection method"
                ></app-text-input>
              </ion-col>
            </ion-row>
          </ion-grid>

          <!-- Section: Procedures (Assembly / Maintenance / Inspection / Disassembly) -->
          <ion-item-divider class="form-section-title" id="procedures-section">
            <ion-label>
              <h2>{{ proceduresSection?.title || "Procedures" }}</h2>
            </ion-label>
          </ion-item-divider>
          <ion-grid>
            <ion-row>
              <ion-col>
                <app-checkbox-input
                  [templateFormGroup]="proceduresCheckboxesFormGroup"
                  [checkboxes]="proceduresCheckboxFields"
                  [label]="
                    proceduresCheckboxGroup?.checkboxLabel ||
                    fieldMappings['procedures']
                  "
                  [requiredField]="true"
                  [disabled]="isFallExposureFieldBlank()"
                ></app-checkbox-input>
              </ion-col>
            </ion-row>
            <ion-row
              *ngIf="
                proceduresCheckboxesFormGroup.get('proceduresOther')?.value
              "
            >
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="proceduresFormGroup"
                  [control]="proceduresFormGroup.get('otherProceduresSpecify')!"
                  [label]="fieldMappings['otherProceduresSpecify']"
                  [requiredField]="
                    proceduresCheckboxesFormGroup.get('proceduresOther')?.value
                  "
                  placeholder="Specify other procedure"
                ></app-text-input>
              </ion-col>
            </ion-row>
          </ion-grid>

          <!-- Section: Methods of Overhead Protection -->
          <ion-item-divider
            class="form-section-title"
            id="overhead-protection-section"
          >
            <ion-label>
              <h2>
                {{ overheadProtectionSection?.title || "Overhead Protection" }}
              </h2>
            </ion-label>
          </ion-item-divider>
          <ion-grid>
            <ion-row>
              <ion-col>
                <app-checkbox-input
                  [templateFormGroup]="overheadProtectionCheckboxesFormGroup"
                  [checkboxes]="overheadProtectionCheckboxFields"
                  [label]="
                    overheadProtectionCheckboxGroup?.checkboxLabel ||
                    fieldMappings['overheadProtection']
                  "
                  [requiredField]="true"
                  [disabled]="isFallExposureFieldBlank()"
                ></app-checkbox-input>
              </ion-col>
            </ion-row>
            <ion-row
              *ngIf="
                overheadProtectionCheckboxesFormGroup.get(
                  'overheadProtectionOther'
                )?.value
              "
            >
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="overheadProtectionFormGroup"
                  [control]="
                    overheadProtectionFormGroup.get(
                      'otherOverheadProtectionSpecify'
                    )!
                  "
                  [label]="fieldMappings['otherOverheadProtectionSpecify']"
                  [requiredField]="
                    overheadProtectionCheckboxesFormGroup.get(
                      'overheadProtectionOther'
                    )?.value
                  "
                  placeholder="Specify other overhead protection measure"
                ></app-text-input>
              </ion-col>
            </ion-row>
          </ion-grid>

          <!-- Section: Rescue Plan -->
          <ion-item-divider class="form-section-title" id="rescue-plan-section">
            <ion-label>
              <h2>{{ rescuePlanSection?.title || "Rescue Plan" }}</h2>
            </ion-label>
          </ion-item-divider>
          <ion-grid>
            <ion-row>
              <ion-col>
                <app-checkbox-input
                  [templateFormGroup]="rescuePlanCheckboxesFormGroup"
                  [checkboxes]="rescuePlanCheckboxFields"
                  [label]="
                    rescuePlanCheckboxGroup?.checkboxLabel ||
                    fieldMappings['rescuePlan']
                  "
                  [requiredField]="true"
                  [disabled]="isFallExposureFieldBlank()"
                ></app-checkbox-input>
              </ion-col>
            </ion-row>
            <ion-row
              *ngIf="
                rescuePlanCheckboxesFormGroup.get('rescuePlanOther')?.value
              "
            >
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="rescuePlanFormGroup"
                  [control]="rescuePlanFormGroup.get('otherRescuePlanSpecify')!"
                  [label]="fieldMappings['otherRescuePlanSpecify']"
                  [requiredField]="
                    rescuePlanCheckboxesFormGroup.get('rescuePlanOther')?.value
                  "
                  placeholder="Specify other rescue plan item"
                ></app-text-input>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ng-container>

  <!-- Section: Working Under Plan (Always Visible) -->
        <ion-item-divider
          class="form-section-title"
          id="working-under-plan-section"
        >
          <ion-label>
            <h2>
              {{ workingUnderPlanSection?.title || "Working Under Plan" }}
            </h2>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-textarea-input
                [templateFormGroup]="workingUnderPlanFormGroup"
                [control]="
                  workingUnderPlanFormGroup.get('techniciansWorkingUnderPlan')!
                "
                [label]="fieldMappings['techniciansWorkingUnderPlan']"
                [requiredField]="requiredFields['techniciansWorkingUnderPlan']"
                [rows]="3"
                placeholder="List technicians working under plan"
              ></app-textarea-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-textarea-input
                [templateFormGroup]="workingUnderPlanFormGroup"
                [control]="workingUnderPlanFormGroup.get('additionalComments')!"
                [label]="fieldMappings['additionalComments']"
                [requiredField]="false"
                [rows]="3"
                placeholder="Enter additional comments"
              ></app-textarea-input>
            </ion-col>
          </ion-row>

          <ion-row>
             <ion-card color="warning" class="information-card">
                <ion-card-content>
                  <ion-text >
                    <h5><ion-icon name="alert-circle"></ion-icon>&nbsp;NEVER CONTINUE WITH A JOB UNLESS YOU ARE CONFIDENT THAT ALL SAFETY ISSUES ARE ADEQUATELY CONTROLLED.  
                      <br><br>IF YOU HAVE ANY DOUBTS DO NOT CONTINUE AND IMMEDIATELY CONTACT YOUR LINE MANAGER OR SUPERVISOR FOR ADVICE </h5> 

                  </ion-text>
                </ion-card-content>
              </ion-card>
          </ion-row>
          <ion-row>
            <ion-card color="primary" class="information-card">
              <ion-card-header
                ><ion-card-title
                  ><ion-icon name="finger-print-outline"></ion-icon
                  >&nbsp;Declaration</ion-card-title
                ></ion-card-header
              >
              <ion-card-content>
                <ion-text>
                  <h4>
                    I declare that I fully understand the safety requirements
                    listed for this job and undertake to comply in full with
                    these requirements at all times while performing my duties
                    and ensure adequate safety supervision of apprentices under
                    my charge
                  </h4>
                </ion-text>
              </ion-card-content>
            </ion-card>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="workingUnderPlanFormGroup"
                [control]="workingUnderPlanFormGroup.get('technicianName')!"
                [label]="fieldMappings['technicianName']"
                [requiredField]="requiredFields['technicianName']"
                placeholder="Enter technician name"
              ></app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>
